import { useNavigate } from 'react-router-dom';
import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  ChangeEvent,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Input,
  iso3ToIso2,
  PhoneInput,
  SkeletonBase,
} from '@unified-checkout/design-system';
import { PenToSquare } from '@unified-checkout/design-system/icons/index';

import type {
  IAccountInfoProps,
  IUpdateEmailPayload,
} from '@app/dashboard/types/innerLayout/profile_details/account-info';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { IFormActionRef } from '@app/dashboard/types/innerLayout/profile_details/footer';
import { useLangStore } from '@app/dashboard/store/lang';
import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import {
  ADD_EMAIL_FIELD_INITIAL_STATE,
  PROFILE_INFO_FIELDS,
} from '@app/dashboard/constants/settings/profile-details';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { notify } from '@app/dashboard/utils/notifier';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import { updateMerchantDetails } from '@app/dashboard/services/inner_layout/profile_details';
import { getMerchantProfileData } from '@app/dashboard/utils/localStorageUtils';
import LabelCardInfo from '@app/dashboard/components/inner_layout/LabelCardInfo';

import styles from './AccountInfo.module.scss';

const AccountInfo = forwardRef(
  (
    { merchantData, setFooterActions }: IAccountInfoProps,
    ref: Ref<IFormActionRef>
  ) => {
    const { t } = useTranslation();
    const { lang } = useLangStore();
    const isFetching = useIsFetching();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [fields, setFields] = useState<IField>(ADD_EMAIL_FIELD_INITIAL_STATE);

    const accountID = getMerchantProfileData().id;

    const { isPending: isSaveChangesLoading, mutate: saveChangesMutation } =
      useMutation({
        mutationFn: ({
          merchantId,
          data,
        }: {
          merchantId: string;
          data: IUpdateEmailPayload;
        }) => updateMerchantDetails(merchantId, data),
        onSuccess: async ({ data }) => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_PROFILE_DETAILS],
          });
          notify.success(
            t('innerLayout.profileDetailsPage.toasters.merchantDataUpdated')
          );

          setFields((prev) => ({
            ...prev,
            [PROFILE_INFO_FIELDS.EMAIL]: {
              ...prev[PROFILE_INFO_FIELDS.EMAIL],
              value: data?.user?.email as string,
              isValid: true,
            },
          }));
        },
        onError: (axiosErr: AxiosError<{ message?: string }>) => {
          let message = t('errors.somethingWentWrong');

          if (axiosErr.response?.data?.message) {
            message = axiosErr.response.data.message;
          } else if (axiosErr.message) {
            message = axiosErr.message;
          }

          notify.error(message);
        },
      });

    const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
      const targetValue = e.target.value || '';
      onFieldChangeHandler(
        fields,
        setFields,
        PROFILE_INFO_FIELDS.EMAIL,
        targetValue,
        () => {}
      );
    };

    const handleNavigateToChangePassword = () => {
      navigate(INNER_ROUTES.CHANGE_PASSWORD);
    };

    const handleCancelChanges = () => {
      setFields((prev) => ({
        ...prev,
        [PROFILE_INFO_FIELDS.EMAIL]: {
          ...prev[PROFILE_INFO_FIELDS.EMAIL],
          value: merchantData?.user?.email || '',
          errorMsg: '',
          isValid: true,
        },
      }));
    };

    const handleSaveChanges = () => {
      // saveChangesMutation({
      //   merchantId: accountID,
      //   data: {
      //     user: {
      //       email: fields[PROFILE_INFO_FIELDS.EMAIL].value as string,
      //     },
      //     password: 'Paymob@123',
      //   },
      // });
    };

    useImperativeHandle(ref, () => ({
      handleCancelChanges,
      handleSaveChanges,
    }));

    useEffect(() => {
      if (merchantData?.user?.email) {
        setFields((prev) => ({
          ...prev,
          [PROFILE_INFO_FIELDS.EMAIL]: {
            ...prev[PROFILE_INFO_FIELDS.EMAIL],
            value: merchantData.user.email,
            isValid: true,
          },
        }));
      }
    }, [merchantData]);

    //TODO: will be used when the user can edit the email and save changes, currently it's view only

    // useEffect(() => {
    //   const isEmailChanged =
    //     merchantData?.user?.email !== fields[PROFILE_INFO_FIELDS.EMAIL].value;

    //   const isEmptyValues = Object.values(fields).some((field) => !field.value);

    //   const shouldDisableCancelBtn =
    //     isEmptyValues || !isEmailChanged || !!isFetching;

    //   const shouldDisablePrimaryBtn =
    //     Object.values(fields).some((field) => field.errorMsg) ||
    //     isEmptyValues ||
    //     !isEmailChanged;

    //   setFooterActions?.({
    //     isLoading: !!isFetching || isSaveChangesLoading,
    //     shouldDisabledPrimary: shouldDisablePrimaryBtn || isSaveChangesLoading,
    //     shouldDisabledSecondary: shouldDisableCancelBtn || isSaveChangesLoading,
    //   });
    // }, [fields, isFetching, isSaveChangesLoading]);

    return (
      <LabelCardInfo label={t('innerLayout.profileDetailsPage.accountInfo')}>
        <div className={styles['account-info-from']}>

          <div className={styles['account-info-from__row']}>
            {isFetching ? (
              <SkeletonBase height="3.5rem" />
            ) : (
              <Input
                label={t('innerLayout.profileDetailsPage.form.companyName')}
                name={PROFILE_INFO_FIELDS.COMPANY_NAME}
                disabled
                value={merchantData?.company_name || ''}
              />
            )}
            {isFetching ? (
              <SkeletonBase height="3.5rem" />
            ) : (
              <PhoneInput
                label={t('innerLayout.profileDetailsPage.form.phoneNumber')}
                name={PROFILE_INFO_FIELDS.PHONE_NUMBER}
                disabled
                phoneNumber={merchantData?.primary_phone_number || ''}
                country={
                  iso3ToIso2(merchantData?.country || '') || undefined
                }
                disableSelectCountry
                onChangeHandler={() => {}}
              />
            )}
          </div>
          <div className={styles['account-info-from__row']}>
            {isFetching ? (
              <SkeletonBase height="3.5rem" />
            ) : (
              <Input
                preventAutoComplete
                label={t('innerLayout.profileDetailsPage.form.email')}
                name={PROFILE_INFO_FIELDS.EMAIL}
                onChange={handleFieldChange}
                value={fields[PROFILE_INFO_FIELDS.EMAIL].value}
                error={
                  fields[PROFILE_INFO_FIELDS.EMAIL].value &&
                  fields[PROFILE_INFO_FIELDS.EMAIL].errorMsg
                }
                status={
                  !!fields[PROFILE_INFO_FIELDS.EMAIL].errorMsg &&
                  fields[PROFILE_INFO_FIELDS.EMAIL].value
                    ? 'error'
                    : 'default'
                }
                disabled
              />
            )}

            {isFetching ? (
              <SkeletonBase height="3.5rem" />
            ) : (
              <div className={styles['account-info-from__row-password']}>
                <Input
                  label={t('innerLayout.profileDetailsPage.form.password')}
                  name={PROFILE_INFO_FIELDS.PASSWORD}
                  value="*********"
                  disabled
                />
                <Button
                  text={t(
                    'innerLayout.profileDetailsPage.actions.changePassword'
                  )}
                  size="md"
                  leadingIcon={PenToSquare}
                  variant="link"
                  onClickHandler={handleNavigateToChangePassword}
                />
              </div>
            )}
          </div>
          {!!isFetching ? (
            <SkeletonBase height="3.5rem" />
          ) : (
            <div className={styles['account-info-from__row-creation']}>
              <span className={styles['account-info-from__row-creation-text']}>
                {t('innerLayout.profileDetailsPage.form.accountCreationDate')}
              </span>
              {(merchantData?.user?.date_joined ||
                merchantData?.created_at) && (
                <span
                  className={styles['account-info-from__row-creation-date']}
                >
                  {formatToLocalizedDateTime(
                    (merchantData?.user?.date_joined ||
                      merchantData?.created_at) as string,
                    lang
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </LabelCardInfo>
    );
  }
);

export default AccountInfo;
